import { SelectionMovie, Selection } from '@funtech-int/kp-graphql-sdk/yango-movies-www';
import { ErrorBoundary, useSignal } from '@funtech-int/rival-react';
import React, { FC, Suspense, useMemo } from 'react';

import { filmRoute } from '@/scenarios/movie/routes';
import * as Showcase from '@/shared/components/Showcase';
import { PageSpinner } from '@/shared/components/PageSpinner';
import { ScrollSentinel } from '@/shared/components/ScrollSentinel/ScrollSentinel';
import { useMovieModal } from '@/shared/hooks/useMovieModal';
import { useProfileManager } from '@/shared/hooks/useProfileManager';
import { RemountOnKey } from '@/shared/components/RemountOnKey/RemountOnKey';

import {
    type MovieOverviewSelection,
    useMovieOverviewQuery,
} from '../../hooks/useMovieOverviewQuery';
import { personRoute } from '../../../person';
import { MovieDetails } from '../MovieDetails/MovieDetails';
import { MovieCardAnalytics } from '../../lib/movieCardAnalytics';

import styles from './Overview.module.css';

interface MovieOverviewProps {
    id: string;
    createSelectionAnalytics: MovieCardAnalytics['createSelectionAnalytics'];
}

const Overview: FC<MovieOverviewProps> = ({ id, createSelectionAnalytics }) => {
    const { selections, pagination, hasMore } = useMovieOverviewQuery({
        contentId: id,
    });
    const profileManager = useProfileManager();

    const movieModal = useMovieModal();

    const controller = useMemo(
        () => ({
            renderItem({
                item,
                index,
                className,
            }: Showcase.RenderItemProps<MovieOverviewSelection>) {
                const analytics = createSelectionAnalytics(item);

                const handleHeaderClick = () => {
                    analytics.selectionNavigated(index);
                };

                const handleCardShow = (movie: SelectionMovie) => {
                    analytics.cardShowed(index, movie, index, profileManager);
                };

                const handleCardClick = (movie: SelectionMovie) => {
                    analytics.cardClicked(index, movie, index, profileManager);
                    movieModal.open(movie.contentId);
                };

                if (item.type === 'director') {
                    return (
                        <Showcase.Selection
                            key={item.id}
                            selection={item as unknown as Selection}
                            movieRoute={filmRoute}
                            location={personRoute({ id: item.id })}
                            onCardClick={handleCardClick}
                            onHeaderClick={handleHeaderClick}
                            onCardShow={handleCardShow}
                            className={className}
                        />
                    );
                }

                if (item.type === 'similar') {
                    return (
                        <Showcase.Selection
                            key={item.id}
                            selection={item as unknown as Selection}
                            movieRoute={filmRoute}
                            onCardClick={handleCardClick}
                            onCardShow={handleCardShow}
                            className={className}
                        />
                    );
                }
                return null;
            },
        }),
        [createSelectionAnalytics, movieModal, profileManager]
    );

    return (
        <div>
            {selections.length > 0 && (
                <Showcase.Layout
                    items={selections}
                    renderItem={controller.renderItem}
                    className={styles.selections}
                />
            )}
            <ScrollSentinel pagination={pagination} direction="end" />

            {!hasMore && <MovieDetails contentId={id} />}
        </div>
    );
};

export const OverviewTab: FC<MovieOverviewProps> = ({ id, createSelectionAnalytics }) => {
    const billingFeaturesCount = useSignal(useProfileManager().billingFeatureNames).length;

    return (
        <RemountOnKey mountKey={billingFeaturesCount}>
            <ErrorBoundary fallback={() => null}>
                <Suspense fallback={<PageSpinner loadMore />}>
                    <Overview
                        id={id}
                        key={id}
                        createSelectionAnalytics={createSelectionAnalytics}
                    />
                </Suspense>
            </ErrorBoundary>
        </RemountOnKey>
    );
};
